import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { req } from "../../utils";
import { Grid } from "@mui/material/";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import fileContractIcon from "../../assets/images/file-contract-regular.svg";
import { differenceInCalendarDays } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function useDeepCompareEffect(effect, deps) {
  const prevDeps = useRef();
  const isFirst = useRef(true);
  if (
    isFirst.current ||
    !prevDeps.current ||
    deps.length !== prevDeps.current.length ||
    !deps.every((dep, i) => {
      if (Array.isArray(dep) && Array.isArray(prevDeps.current[i])) {
        return dep.length === prevDeps.current[i].length && dep.every((v, j) => v === prevDeps.current[i][j]);
      }
      return dep === prevDeps.current[i];
    })
  ) {
    isFirst.current = false;
    prevDeps.current = deps;
    effect();
  }
}

export const PipedriveDeals  = ({ filterProps }) => {
  const { dateRanges, pageFilterByType } = filterProps;
  const [dealStats, setDealStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const purplePalette = [
    "#B3A6FF",  // Lavender
    "#9C6BFF",  // Amethyst
    "#6F32C8"   // Royal Purple
  ];
  
  const bluePalette = [
    "#6EC6FF",  // Sky Blue
    "#5AA2D4",  // Ocean Blue
    "#274A78"   // Midnight Blue
  ];
  
  const orangePalette = [
    "#FFC19E",  // Soft Peach
    "#FF8A3D",  // Amber
    "#D45A2A"   // Burnt Orange
  ];

  const fetchDealsData = async () => {
    setIsLoading(true);
    try {
      const startDate = dateRanges
        ? moment(dateRanges[0]).unix()
        : moment().subtract(30, "days").unix();
      const endDate = dateRanges
        ? moment(dateRanges[1]).unix()
        : moment().unix();
      const url = `get_deals?s=${startDate}&e=${endDate}`;
      const data = await req("GET", url);
      //console.log("Pipedrive deals", data);
      if (data && data.length > 0) {
        const startDateParam = moment.unix(startDate).format("YYYY-MM-DD");
        const endDateParam = moment.unix(endDate).format("YYYY-MM-DD");
        setDealStats(generateDealStats(data, startDateParam, endDateParam));
      }
    } catch (error) {
      console.error("Error fetching leads data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useDeepCompareEffect(() => {
    fetchDealsData();
  }, [dateRanges]);

  // Determine grouping granularity
  const days = dateRanges && dateRanges.length === 2 ? differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0])) : 0;
  const groupByMonth = days > 31;
  const getGroupKey = (dateStr) => {
    const date = new Date(dateStr);
    return groupByMonth
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      : date.toISOString().split('T')[0];
  };

  function generateDealStats(dealsData, startDate = null, endDate = null) {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : new Date();

    // Filter deals based on the date range
    const dealsInRange = dealsData.filter(deal => {
        const dealDate = new Date(deal.add_time);
        return (!start || dealDate >= start) && dealDate <= end;
    });

    // Add console log to inspect deal.user_id
    if (dealsInRange.length > 0 && dealsInRange[0].user_id) {
      console.log("PipedriveDeals - deal.user_id:", dealsInRange[0].user_id);
    }

    // Generate deals per group per BDM data
    const dealsPerGroupPerBDM = dealsInRange.reduce((acc, deal) => {
      const groupKey = getGroupKey(deal.add_time);
      const bdmName = deal.user_id.name;
      if (!acc[groupKey]) acc[groupKey] = {};
      acc[groupKey][bdmName] = (acc[groupKey][bdmName] || 0) + 1;
      return acc;
    }, {});

    // Total number of deals
    const totalDeals = dealsInRange.length;

    // Deals by BDM (user_id.name)
    const dealsByBDM = dealsInRange.reduce((acc, deal) => {
        const bdmName = deal.user_id.name;
        acc[bdmName] = (acc[bdmName] || 0) + 1;
        return acc;
    }, {});

    // Most recent deal
    const mostRecentDeal = dealsInRange.reduce((latest, deal) => {
        const dealDate = new Date(deal.add_time);
        return dealDate > new Date(latest.add_time) ? deal : latest;
    }, dealsInRange[0]);

    const data = [
        { label: "Total Deals", value: totalDeals },
        { label: "Deals by BDM", value: dealsByBDM },
        { label: "Most Recent Deal", value: mostRecentDeal },
        { label: "Deals per Day per BDM", value: dealsPerGroupPerBDM },
    ];
  
    console.log("dealStats", data);
    return data;
  }

  const renderDealsPerDayChart = () => {
    if (!dealStats) return null;

    const dealsPerGroupPerBDM = dealStats.find(stat => stat.label === "Deals per Day per BDM")?.value || {};
    const groupKeys = Object.keys(dealsPerGroupPerBDM).sort();
    const bdms = [...new Set(groupKeys.flatMap(date => Object.keys(dealsPerGroupPerBDM[date])))]

    const bdmColorMap = {
      "kirk": purplePalette[0],  // Lavender
      "Dennis": bluePalette[0],  // Ocean Blue
      "Matt Miliano": orangePalette[0],  // Amber
      "Brad": purplePalette[1], // Amethyst
      "Frank": bluePalette[1] // Ocean Blue
    };

    const datasets = bdms.map((bdm) => {
      const data = groupKeys.map(date => dealsPerGroupPerBDM[date][bdm] || 0);
      const hasDeals = data.some(value => value > 0);

      return {
        label: bdm,
        data: data,
        backgroundColor: bdmColorMap[bdm] || "#B3A6FF", // Default to Lavender if not specified
        hidden: !hasDeals, // Hide the dataset if there are no deals
      };
    }).filter(dataset => !dataset.hidden); // Remove hidden datasets from the final array

    const chartData = {
      labels: groupKeys,
      datasets: datasets,
    };

    const options = {
      responsive: true,
      scales: {
        x: {
          stacked: true,
          type: 'time',
          time: {
            unit: (() => {
              if (dateRanges && dateRanges.length === 2) {
                const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
                return days > 31 ? 'month' : 'day';
              }
              return 'day';
            })()
          }
        },
        y: { stacked: true }
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
        },
        title: {
          display: true,
          text: 'Deals per Day by BDM',
        },
      },
    };

    return <Bar data={chartData} options={options} />;
  };

  const formatDate = (date) => moment(date).format("DD/MM/YYYY");

  if (isLoading) return (
    <Grid
      item
      xs={12}
      sm={12}
      md={6}
      lg={6}
      className={`${
        pageFilterByType !== "unfiltered" && pageFilterByType !== "contract"
          ? "d-none"
          : ""
      }`}
    >
      <div className="card card-stat-summary animate-bg-primary">
        <div className="card-body">
          <div className="card-stat-summary--title">Pipeline Deals</div>
          <div className="card-stat-summary--total-wrapper">
            <div>Loading...</div>
          </div>
        </div>
        <div className="card-bg">
          <img
            src={fileContractIcon}
            alt="File Contract Icon"
            className="bg-icon"
          />
        </div>
      </div>
    </Grid>
  );
  if (!dealStats) return null;

  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={6}
      lg={6}
      className={`${
        pageFilterByType !== "unfiltered" && pageFilterByType !== "contract"
          ? "d-none"
          : ""
      }`}
    >
      <div className="card card-stat-summary">
        <div className="card-body">
          <div className="card-stat-summary--title">PipeDrive Deals</div>
          <div className="card-stat-summary--total-wrapper"></div>
          <Grid container spacing={2}>
              <Grid item xs={12}  sm={12} md={6}>
                <div className="card-stat-summary--total">
                  {dealStats[0].value}
                  <div className="card-stat-summary--total-subtitle">
                    Total deals
                  </div>
                </div>
              </Grid>
            </Grid>
          {renderDealsPerDayChart()}
          {dealStats[2].value && (
            <div className="most-recent-deal" style={{ marginTop: '1rem' }}>
              <h4>Most Recent Deal</h4>
              <p><strong>Title:</strong> {dealStats[2].value.title}</p>
              <p><strong>Added:</strong> {formatDate(dealStats[2].value.add_time)}</p>
              <p><strong>BDM:</strong> {dealStats[2].value.owner_name}</p>
            </div>
          )}
          <div className="card-stat-summary--meta">
            <div className="card-stat-summary--meta-timeline">
              <span className="text-persiangreen">Date range:</span>{" "}
              {formatDate(dateRanges[0])} - {formatDate(dateRanges[1])}
            </div>
          </div>
        </div>
        <div className="card-bg">
          <img
            src={fileContractIcon}
            alt="File Contract Icon"
            className="bg-icon"
          />
        </div>
      </div>
    </Grid>
  );
};
